import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ApolloError, useQuery } from '@apollo/client';
import { ALL_MISSIONS } from '@bloomays-lib/adapter.api-bloomer';
import { WithErrorBoundary } from '@bloomays-lib/ui.shared';
import { AllMissionGQLItemResult } from '@bloomays-lib/types.shared';

import MissionsDashboard from '../organisms/MissionsDashboard';
import { notify } from '../../helpers/toastify';
import { styled } from '@mui/material/styles';
import { PageHeader } from '../molecules/PageHeader';
import { translateApolloError } from '@bloomays-lib/utils.shared';
import { errorLogger } from '../../helpers/error';

/**
 * Dashboard des missions freelance (équipe interne / Bloomies) — route /missions-freelance.
 *
 * Cette page se charge uniquement de récupérer les données : toutes les missions
 * signées sont chargées (sans filtre isActive), puis l'organisme MissionsDashboard
 * calcule KPIs, filtres et onglets côté front à partir de ce dataset (TEC-3032).
 */
const FreelanceMissionsDashboard = () => {
  const { t } = useTranslation(['notify']);
  const {
    error: errorMissions,
    data: dataMissions,
    loading: loadingMissions,
    refetch: refetchMissions,
  } = useQuery<{
    allMissions: { results: AllMissionGQLItemResult[] };
  }>(ALL_MISSIONS);

  const notifyError = (e: ApolloError) => {
    errorLogger(e, {
      extraInfos: 'Internal server error, impossible to retrieve data about bloomers and their infos !',
    });
    // Message FR dérivé du code d'erreur plutôt que le message technique brut (TEC-3053).
    notify('error', translateApolloError(e, t));
  };

  useEffect(() => {
    if (!errorMissions) return;
    notifyError(errorMissions);
  }, [errorMissions]);

  return (
    <Page>
      <PageHeader
        id="titleMissionsDashboard"
        title="Pilotage des missions"
        subtitle="Vue d'ensemble financière et opérationnelle de toutes nos missions signées."
        refreshLabel="Actualiser"
        lastUpdatedPrefix="Mis à jour"
        onRefresh={async () => {
          await refetchMissions();
        }}
      />
      <MissionsDashboard loading={loadingMissions} dataMissions={dataMissions?.allMissions?.results || []} />;
    </Page>
  );
};

const Page = styled('div')(() => ({
  width: '90%',
  margin: 'auto',
}));

export default WithErrorBoundary(FreelanceMissionsDashboard);
